"use client"

import { useState, useCallback, useEffect, useRef } from "react"
import { ConversationProvider, useConversation } from "@elevenlabs/react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { Alert, AlertDescription } from "@/components/ui/alert"
import {
  Mic,
  MicOff,
  Phone,
  PhoneOff,
  Loader2,
  AlertCircle,
  Volume2,
} from "lucide-react"
import { useAuth } from "@/hooks/use-auth"
import { CUSTOM_INSTRUCTIONS_DYNAMIC_VARIABLE } from "@/lib/voice/caller-context"

interface AgentPreviewDialogProps {
  agentId: string  // UUID
  agentName: string
  open: boolean
  onOpenChange: (open: boolean) => void
  experience?: "dialog" | "onboarding"
  withConversationProvider?: boolean
}

const DEFAULT_PREVIEW_INPUTS = {
  callerNumber: "+15551234567",
  customInstructions: "",
}

export function AgentPreviewDialog({
  withConversationProvider = true,
  ...props
}: AgentPreviewDialogProps) {
  if (!withConversationProvider) {
    return <AgentPreviewDialogContent {...props} />
  }

  return (
    <ConversationProvider>
      <AgentPreviewDialogContent {...props} />
    </ConversationProvider>
  )
}

function AgentPreviewDialogContent({
  agentId,
  agentName,
  open,
  onOpenChange,
  experience = "dialog",
}: AgentPreviewDialogProps) {
  const { fetchWithAuth, user } = useAuth()
  const tenantId = user?.impersonated_tenant_id ?? user?.tenant_id
  const preferencesKey = experience === "dialog" && user?.user_id && tenantId
    ? `versafy:voice-preview:v1:${user.user_id}:${tenantId}:${agentId}`
    : null
  const [isConnecting, setIsConnecting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Emulate caller number for the preview session. Real calls get the caller's
  // number from the PBX at call registration; the preview passes this value
  // as the same `caller_number` dynamic variable, so the in-conversation
  // number context is testable without a PSTN call. Empty → "unknown".
  const [testCallerNumber, setTestCallerNumber] = useState(DEFAULT_PREVIEW_INPUTS.callerNumber)
  const [customInstructions, setCustomInstructions] = useState("")
  useEffect(() => {
    if (!open) return
    const saved = readPreviewInputs(preferencesKey)
    setTestCallerNumber(saved.callerNumber)
    setCustomInstructions(saved.customInstructions)
  }, [open, preferencesKey])
  // Tracks the in-flight startSession flow so that closing the dialog or
  // unmounting cancels the pending fetch and prevents an orphaned WebRTC
  // session (mic + LiveKit room) from being opened after the UI is gone.
  const startAbortRef = useRef<AbortController | null>(null)

  // Destructure stable methods + state from the hook. `useConversation`
  // returns a fresh object literal on every render, so depending on the
  // whole `conversation` value in effects/callbacks would re-fire them on
  // any state change (e.g. setIsConnecting) and tear down the session we
  // just started. Methods are stable, state changes drive the UI.
  const { status, isSpeaking, startSession, endSession } = useConversation({
    onConnect: () => {
      setIsConnecting(false)
      setError(null)
    },
    onDisconnect: () => {
      setIsConnecting(false)
      if (experience === "onboarding") onOpenChange(false)
    },
    onError: (message: string) => {
      console.error("Conversation error:", message)
      setError(message || "An error occurred during the conversation")
      setIsConnecting(false)
      if (experience === "onboarding") onOpenChange(false)
    },
    onMessage: () => {
      // Message received
    },
  })

  const getAgentState = () => {
    if (status === "connected") {
      return isSpeaking ? "speaking" : "listening"
    }
    return "disconnected"
  }

  const agentState = getAgentState()

  const handleStart = useCallback(async () => {
    savePreviewInputs(preferencesKey, {
      callerNumber: testCallerNumber.trim(),
      customInstructions: customInstructions.trim(),
    })
    // Replace any prior in-flight attempt with a fresh controller.
    startAbortRef.current?.abort()
    const controller = new AbortController()
    startAbortRef.current = controller
    const { signal } = controller

    setError(null)
    setIsConnecting(true)

    try {
      // Request mic permission with the same constraints the ElevenLabs SDK
      // uses internally, then release the track so the SDK can open its own
      // stream. Without this, leaving an open `{audio: true}` track around
      // creates a parallel mic capture without echo cancellation, which feeds
      // TTS playback back into the input and breaks barge-in detection.
      const permissionStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      })
      permissionStream.getTracks().forEach((track) => track.stop())
      if (signal.aborted) return

      // Get a WebRTC conversation token from our API. WebRTC (LiveKit under
      // the hood) gives lower latency and proper AEC vs the old WebSocket
      // signed-url path, which makes barge-in / interruption work properly.
      const response = await fetchWithAuth(
        `/api/agents/voice/${agentId}/conversation-token`,
        { signal }
      )
      if (signal.aborted) return

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || "Failed to get conversation token")
      }

      const { token } = await response.json()
      if (signal.aborted) return

      // Start conversation. In @elevenlabs/react 1.x startSession is sync;
      // connection errors arrive via the onError callback above.
      // `dynamicVariables` is the client SDK's camelCase key (mapped to
      // `dynamic_variables` internally) — distinct from the snake_case
      // server-side PBX register-call payload.
      startSession({
        conversationToken: token,
        connectionType: "webrtc",
        dynamicVariables: {
          caller_number: testCallerNumber.trim() || "unknown",
          ...(experience === "dialog" && {
            [CUSTOM_INSTRUCTIONS_DYNAMIC_VARIABLE]: customInstructions.trim(),
          }),
        },
      })
    } catch (err) {
      if ((err as { name?: string })?.name === "AbortError") return
      console.error("Failed to start conversation:", err)
      setError(err instanceof Error ? err.message : "Failed to start conversation")
      setIsConnecting(false)
      if (experience === "onboarding") onOpenChange(false)
    }
  }, [agentId, customInstructions, experience, fetchWithAuth, onOpenChange, preferencesKey, startSession, testCallerNumber])

  const handleEnd = useCallback(() => {
    startAbortRef.current?.abort()
    try {
      endSession()
    } catch (err) {
      console.error("Failed to end conversation:", err)
    }
  }, [endSession])

  const handleClose = useCallback(() => {
    // Abort any pending startSession flow first so a late-arriving connection
    // attempt cannot leak the mic / LiveKit room after the dialog closes.
    startAbortRef.current?.abort()
    try {
      // endSession is safe to call regardless of status; if there is no live
      // session it's a no-op. Calling it unconditionally also covers the
      // window where startSession has fired but onConnect has not, which is
      // exactly the leak we want to prevent.
      endSession()
    } catch (e) {
      console.error("Error ending conversation:", e)
    } finally {
      setError(null)
      setIsConnecting(false)
      onOpenChange(false)
    }
  }, [endSession, onOpenChange])

  const previousOpenRef = useRef(false)
  useEffect(() => {
    if (experience !== "onboarding") return

    const wasOpen = previousOpenRef.current
    previousOpenRef.current = open
    if (open && !wasOpen) {
      void handleStart()
      return
    }
    if (!open && wasOpen) handleEnd()
  }, [experience, handleEnd, handleStart, open])

  // Keep a ref to the latest endSession so the unmount cleanup below has
  // empty deps and runs ONLY on real unmount — not on every rerender that
  // returns a new method reference from `useConversation`.
  const endSessionRef = useRef(endSession)
  endSessionRef.current = endSession

  // Safety net: if the parent unmounts the dialog without calling handleClose
  // (e.g. route change while Connecting...), still abort the pending flow and
  // tear down any session that may have started.
  useEffect(() => {
    return () => {
      startAbortRef.current?.abort()
      try {
        endSessionRef.current()
      } catch {
        // best-effort cleanup
      }
    }
  }, [])

  if (experience === "onboarding") {
    return (
      <div aria-live="polite">
        {error ? (
          <Alert variant="destructive" className="mt-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : (
          <span className="sr-only">
            {getOnboardingStatusMessage(isConnecting, open)}
          </span>
        )}
      </div>
    )
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="sm:max-w-md max-h-[90dvh] overflow-y-auto"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle><span>Test Agent:</span> {agentName}</DialogTitle>
          <DialogDescription>
            Talk to your voice agent using your microphone
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Visual Indicator */}
          <div className="flex justify-center">
            <div
              className={`w-32 h-32 rounded-full flex items-center justify-center transition-all duration-300 ${
                agentState === "speaking"
                  ? "bg-blue-500 scale-110 animate-pulse"
                  : agentState === "listening"
                  ? "bg-green-500 scale-105"
                  : "bg-gray-200"
              }`}
            >
              {agentState === "speaking" ? (
                <Volume2 className="w-12 h-12 text-white" />
              ) : agentState === "listening" ? (
                <Mic className="w-12 h-12 text-white" />
              ) : (
                <MicOff className="w-12 h-12 text-gray-400" />
              )}
            </div>
          </div>

          {/* Status Text */}
          <div className="text-center">
            <p className="text-sm font-medium text-muted-foreground">
              {isConnecting
                ? "Connecting..."
                : agentState === "speaking"
                ? "Agent is speaking..."
                : agentState === "listening"
                ? "Listening to you..."
                : "Ready to start"}
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Emulate caller number — simulates the caller ID a real call gets
              from the PBX. Editable only before the session starts. */}
          <div className="space-y-1.5">
            <Label htmlFor="preview-caller-number">Emulate caller number (optional)</Label>
            <Input
              id="preview-caller-number"
              type="tel"
              placeholder="+15551234567"
              value={testCallerNumber}
              onChange={(e) => setTestCallerNumber(e.target.value)}
              disabled={isConnecting || status === "connected"}
            />
            <p className="text-xs text-muted-foreground">
              Passed to the agent as the caller ID. Leave empty to simulate an
              unknown caller.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="preview-custom-instructions">Custom instructions (optional)</Label>
            <Textarea
              id="preview-custom-instructions"
              value={customInstructions}
              onChange={(event) => setCustomInstructions(event.target.value)}
              placeholder="Customer name, delivery address, or instructions for this test call."
              rows={3}
              maxLength={2000}
              disabled={isConnecting || status === "connected"}
              aria-describedby="preview-custom-instructions-description"
            />
            <p id="preview-custom-instructions-description" className="text-xs text-muted-foreground">
              <span>Extra context for this test call only. Does not change the agent’s opening message or saved prompt.</span>
              {" "}<span>The phone number and instructions used with Start Call are remembered for this agent in this browser.</span>
            </p>
          </div>

          {/* Controls */}
          <div className="flex justify-center gap-4">
            {status !== "connected" ? (
              <Button
                onClick={handleStart}
                disabled={isConnecting}
                className="bg-green-600 hover:bg-green-700"
                size="lg"
              >
                {isConnecting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <Phone className="w-5 h-5 mr-2" />
                    Start Call
                  </>
                )}
              </Button>
            ) : (
              <Button
                onClick={handleEnd}
                variant="destructive"
                size="lg"
              >
                <PhoneOff className="w-5 h-5 mr-2" />
                <span>End Call</span>
              </Button>
            )}
          </div>

          {/* Instructions */}
          <div className="text-center text-xs text-muted-foreground">
            <p>Make sure your microphone is enabled and working.</p>
            <p>Click &quot;Start Call&quot; to begin the conversation.</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function readPreviewInputs(key: string | null): typeof DEFAULT_PREVIEW_INPUTS {
  if (!key) return DEFAULT_PREVIEW_INPUTS
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return DEFAULT_PREVIEW_INPUTS
    const saved: unknown = JSON.parse(raw)
    if (
      saved && typeof saved === "object"
      && "callerNumber" in saved && typeof saved.callerNumber === "string"
      && "customInstructions" in saved && typeof saved.customInstructions === "string"
      && saved.customInstructions.length <= 2000
    ) {
      return { callerNumber: saved.callerNumber, customInstructions: saved.customInstructions }
    }
  } catch {
    // Storage may be unavailable or contain malformed data; testing still works.
  }
  return DEFAULT_PREVIEW_INPUTS
}

function savePreviewInputs(key: string | null, inputs: typeof DEFAULT_PREVIEW_INPUTS) {
  if (!key) return
  try {
    localStorage.setItem(key, JSON.stringify(inputs))
  } catch {
    // Remembering test inputs is optional; never prevent starting a call.
  }
}

function getOnboardingStatusMessage(isConnecting: boolean, open: boolean) {
  if (isConnecting) return "Connecting to agent"
  return open ? "Voice conversation active" : "Voice conversation ended"
}
